import React, { useEffect, useState } from 'react';
import { secureGet } from '../../config/api';
import { useTranslation } from 'react-i18next';

const SidebarZoneEvents = ({ open, onClose, zoneId, zoneName, zoneManagerName, onDetails }) => {
  const { t } = useTranslation();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && zoneId) {
      fetchZoneEvents();
    }
  }, [open, zoneId]);

  const fetchZoneEvents = async () => {
    setLoading(true);
    try {
      const response = await secureGet(`/inventory-zones/${zoneId}/events`);
      console.log(response.data);   
      setEvents(response.data || []);
      
    } catch (error) {
      console.error('Error fetching zone events:', error);
      setEvents([]);
    } finally {
      setLoading(false);
    }
  };

  const getEventStatusColor = (event) => {
    console.log(event);
    switch (event?.toLowerCase()) {
      case 'zone_status_changed':
        return '#CBCBCD';
      case 'location_status_changed':
        return '#FEA26F';
      case 'assigned_for_counting':
        return '#06D6A0';
      case 'assigned_for_control':
        return '#0283F4';
      default:
        return '#CBCBCD';
    }
  };

  const formatDateTime = (dateTime) => {
    if (!dateTime) return '';
    const date = new Date(dateTime);
    return date.toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const getEventLabel = (event) => {
    // Map event types to French labels
    const eventLabels = {
      'ASSIGNED': 'Comptage Assigné',
      'STARTED': 'Comptage Démarré',
      'COMPLETED': 'Comptage Terminé',
      'CREATED': 'Comptage Créé',
      'SUBMITTED': 'Comptage Soumis',
      'CONTROLLED': 'Comptage Contrôlé',
      'VALIDATED': 'Comptage Validé'
    };
    
    return eventLabels[event] || event || 'Événement';
  };

  if (!open) return null;

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 z-[2050] flex justify-end"
      onClick={onClose}
    >
      <div 
        className="bg-[#F9FAFC] w-[40%] h-full shadow-xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className=" pt-6 px-8">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-[18px] font-bold text-[#0283F4] mb-1">
                {zoneManagerName || 'RESPONSABLE ZONE'}
              </p>
              <h2 className="text-[32px] font-semibold text-[#445E76]">
                {zoneName || 'NOM DU PLAN'}
              </h2>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 p-6 overflow-y-auto">
          {loading ? (
            <div className="flex justify-center items-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : (
            <div className="relative">
              {events.length > 0 && (
                <div 
                  className="absolute left-3 w-0.5 bg-gray-300"
                  style={{
                    top: '50%', 
                    height: `${(events.length - 1) * 150}px`,
                    transform: 'translateY(-50%)' 
                  }}
                ></div>
              )}
              
              {/* Events */}
              <div className="space-y-6">
                {events.length > 0 ? (
                  events.map((event, index) => (
                    <div key={event.id || index} className="relative flex items-start">
                      {/* Timeline dot */}
                      <div 
                        className="absolute w-3 h-3 rounded-full z-10"
                        style={{ 
                          backgroundColor: getEventStatusColor(event.event),
                          left: '13px', // Centered on the timeline line
                          top: '50%',
                          transform: 'translate(-50%, -50%)'
                        }}
                      ></div>
                      
                      {/* Event card */}
                      <div className="ml-8 bg-white rounded-[12px] shadow-[0px_20px_80px_#00000011]  p-4 w-full">
                        {/* Date on first line */}
                        <div className="text-[16px] font-semibold text-[#6A78F8] mb-1">
                          {formatDateTime(event.date)}
                        </div>
                        
                        {/* Full sentence on second line */}
                        <div className="text-[16px] text-[#38485C] mb-1">
                          {event.userName && (
                            <span className="text-[#043F88] font-semibold">{event.userName} </span>
                          )}
                          {event.event === 'ZONE_STATUS_CHANGED' ? (
                            <>
                              {event.oldValue && event.newValue ? (
                                <>a <span className="text-[#043F88] font-semibold">changé le statut de la zone</span> de "<span className="text-[#043F88] font-semibold">{t(`inventory_status.${event.oldValue}`)}</span>" à "<span className="text-[#043F88] font-semibold">{t(`inventory_status.${event.newValue}`)}</span>"</>
                              ) : event.newValue ? (
                                <>a <span className="text-[#043F88] font-semibold">changé le statut de la zone</span> à "<span className="text-[#043F88] font-semibold">{t(`inventory_status.${event.newValue}`)}</span>"</>
                              ) : (
                                t(`zone_events.${event.event}`)
                              )}
                            </>
                          ) : event.event === 'LOCATION_STATUS_CHANGED' ? (
                            <>
                              {event.oldValue && event.newValue ? (
                                <>a <span className="text-[#043F88] font-semibold">changé le statut de l'emplacement</span> de "<span className="text-[#043F88] font-semibold">{t(`inventory_status.${event.oldValue}`)}</span>" à "<span className="text-[#043F88] font-semibold">{t(`inventory_status.${event.newValue}`)}</span>"</>
                              ) : event.newValue ? (
                                <>a <span className="text-[#043F88] font-semibold">changé le statut de l'emplacement</span> à "<span className="text-[#043F88] font-semibold">{t(`inventory_status.${event.newValue}`)}</span>"</>
                              ) : (
                                t(`zone_events.${event.event}`)
                              )}
                            </>
                          ) : event.event === 'ASSIGNED_FOR_COUNTING' ? (
                            <>a <span className="text-[#043F88] font-semibold">assigné pour comptage</span>: <span className="text-[#043F88] font-semibold">{event.newValue || t(`zone_events.${event.event}`)}</span></>
                          ) : event.event === 'ASSIGNED_FOR_CONTROL' ? (
                            <>a <span className="text-[#043F88] font-semibold">assigné pour contrôle</span>: <span className="text-[#043F88] font-semibold">{event.newValue || t(`zone_events.${event.event}`)}</span></>
                          ) : (
                            <>
                              {event.oldValue && event.newValue ? (
                                <>a <span className="text-[#043F88] font-semibold">changé</span> de "<span className="text-[#043F88] font-semibold">{event.oldValue}</span>" à "<span className="text-[#043F88] font-semibold">{event.newValue}</span>"</>
                              ) : event.newValue ? (
                                <>a <span className="text-[#043F88] font-semibold">défini</span>: <span className="text-[#043F88] font-semibold">{event.newValue}</span></>
                              ) : (
                                t(`zone_events.${event.event}`)
                              )}
                            </>
                          )}
                        </div>
                        
                        {/* Emplacement on third line */}
                        {event.locationName && (
                          <div className="text-[14px] text-[#38485C]">
                            Emplacement: {event.locationName}
                          </div>
                        )}
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center text-gray-500 py-8">
                    
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer with Details Button */}
        <div className="p-6">
          <button
            onClick={onDetails}
            className="w-full bg-[#043F88] text-white font-bold text-[21px] py-3 px-4 rounded-[12px] transition-colors duration-200"
          >
            {t("actions.details")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SidebarZoneEvents;
